/**
 * ArBundleNameInput
 *
 * The group header's name field — an always-present borderless input replacing
 * the old rename popover (SYD-2307). Sole consumer is ArBundleHeaderRow, which
 * must mount it with `key={bundleLabel}` : the draft is seeded once from the
 * prop, so an external rename (undo, collaborator, the optimistic write landing)
 * reaches the field only through that remount, never through a sync effect.
 * Commits on blur alone — `updateBundleValue` records one undo entry and one
 * undebounced write per call — and only when the trimmed draft differs. An empty
 * draft reverts, keeping the "a bundle always has a name" invariant. The header
 * cell carries no `artable-cell-` id, so grid navigation keys are kept from the
 * table's keydown handler, which would otherwise move the last focused cell.
 */

import { FC, KeyboardEvent, memo, useContext, useState } from 'react';
import { Input, theme } from 'antd';
import { ArTableActionsContext } from '../context';
import type { ArRowId } from '#types';

type ArBundleNameInputProps = {
  rowId: ArRowId;
  bundleLabel: string;
  disabled?: boolean;
};

const GRID_NAVIGATION_KEYS: ReadonlySet<string> = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
]);

const ArBundleNameInput: FC<ArBundleNameInputProps> = ({ rowId, bundleLabel, disabled = false }) => {
  const { token } = theme.useToken();
  const actions = useContext(ArTableActionsContext)!;
  const [draft, setDraft] = useState(bundleLabel);

  const handleBlur = (): void => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setDraft(bundleLabel);
      return;
    }
    setDraft(trimmed);
    if (trimmed !== bundleLabel) {
      actions.updateBundleValue(rowId, trimmed);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      setDraft(bundleLabel);
      return;
    }
    if (GRID_NAVIGATION_KEYS.has(e.key)) e.stopPropagation();
  };

  return (
    <Input
      aria-label={`artable-bundle-name-${rowId}`}
      variant="borderless"
      disabled={disabled}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={handleBlur}
      onPressEnter={(e) => {
        e.preventDefault();
        e.currentTarget.blur();
      }}
      onKeyDown={handleKeyDown}
      style={{ flex: 1, minWidth: 0, fontWeight: token.fontWeightStrong, padding: 0 }}
    />
  );
};

export default memo(ArBundleNameInput);
